import { useState, useCallback } from "react";
import {
  VendorEntity,
  CreateVendorUseCase,
  UpdateVendorUseCase,
} from "../../domain";
import { VendorRepositoryImpl } from "../../data/repositories/VendorRepositoryImpl";

// Create repository instance (singleton)
const vendorRepository = new VendorRepositoryImpl();

// Create use cases
const createVendorUseCase = new CreateVendorUseCase(vendorRepository);
const updateVendorUseCase = new UpdateVendorUseCase(vendorRepository);

export interface UseVendorOperationsReturn {
  loading: boolean;
  error: string | null;

  createVendor: (
    vendorData: Partial<VendorEntity>
  ) => Promise<VendorEntity | null>;
  updateVendor: (
    vendorId: string,
    vendorData: Partial<VendorEntity>
  ) => Promise<VendorEntity | null>;

  clearError: () => void;
}

export const useVendorOperations = (): UseVendorOperationsReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Create a new vendor
   */
  const createVendor = useCallback(
    async (vendorData: Partial<VendorEntity>): Promise<VendorEntity | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await createVendorUseCase.execute(vendorData);

        if (!result.success || !result.data) {
          throw new Error(result.error || "Failed to create vendor");
        }

        return result.data;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to create vendor";
        setError(errorMessage);
        console.error("Failed to create vendor:", error);
        // Re-throw the error so caller can handle it
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Update an existing vendor
   */
  const updateVendor = useCallback(
    async (
      vendorId: string,
      vendorData: Partial<VendorEntity>
    ): Promise<VendorEntity | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await updateVendorUseCase.execute(vendorId, vendorData);

        if (!result.success || !result.data) {
          throw new Error(result.error || "Failed to update vendor");
        }

        return result.data;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to update vendor";
        setError(errorMessage);
        console.error("Failed to update vendor:", error);
        // Re-throw the error so caller can handle it
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    createVendor,
    updateVendor,
    clearError,
  };
};
